import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [features, setFeatures] = useState({});
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refresh token function
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;
    
    try {
      const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
      const { access_token, refresh_token, features: newFeatures, subscription_status } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      
      // Update features if returned
      if (newFeatures) {
        setFeatures(newFeatures);
        localStorage.setItem('features', JSON.stringify(newFeatures));
      }
      if (subscription_status) {
        setSubscriptionStatus(subscription_status);
        localStorage.setItem('subscription_status', subscription_status);
      }
      
      return true;
    } catch (error) {
      // Refresh failed, logout
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('features');
      localStorage.removeItem('subscription_status');
      setUser(null);
      setFeatures({});
      setSubscriptionStatus(null);
      return false;
    }
  }, []);

  // Re-read the tenant's current plan access from the server (plans/trials can change after login)
  const refreshFeatures = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const { data } = await api.get('/subscription/my');
      if (data?.features) {
        setFeatures(data.features);
        localStorage.setItem('features', JSON.stringify(data.features));
      }
      if (data?.status) {
        setSubscriptionStatus(data.status);
        localStorage.setItem('subscription_status', data.status);
      }
    } catch (e) {
      // keep the cached access if the refresh fails
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedFeatures = localStorage.getItem('features');
    const savedSubStatus = localStorage.getItem('subscription_status');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    if (savedFeatures) {
      try {
        setFeatures(JSON.parse(savedFeatures));
      } catch (e) {
        setFeatures({});
      }
    }
    if (savedSubStatus) {
      setSubscriptionStatus(savedSubStatus);
    }
    setLoading(false);
    if (token) refreshFeatures();
    
    // Set up token refresh interval (every 20 minutes)
    const refreshInterval = setInterval(() => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        refreshAccessToken();
      }
    }, 20 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [refreshAccessToken, refreshFeatures]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { 
        access_token, 
        refresh_token, 
        user, 
        tenant_slug, 
        redirect_url,
        features: loginFeatures,
        subscription_status: subStatus
      } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      if (tenant_slug) {
        localStorage.setItem('tenant_slug', tenant_slug);
      }
      if (loginFeatures) {
        localStorage.setItem('features', JSON.stringify(loginFeatures));
        setFeatures(loginFeatures);
      }
      if (subStatus) {
        localStorage.setItem('subscription_status', subStatus);
        setSubscriptionStatus(subStatus);
      }
      setUser(user);
      
      return { success: true, tenant_slug, redirect_url };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('features');
    localStorage.removeItem('subscription_status');
    setUser(null);
    setFeatures({});
    setSubscriptionStatus(null);
  };

  // Feature check helper
  const hasFeature = useCallback((featureKey) => {
    const feature = features[featureKey];
    return feature?.enabled ?? false;
  }, [features]);

  // Get feature limit
  const getFeatureLimit = useCallback((featureKey) => {
    const feature = features[featureKey];
    return feature?.limit ?? null;
  }, [features]);

  // Role checks
  const isAdmin = user?.role === 'AGENCY_ADMIN' || user?.role === 'SUPER_ADMIN';
  const isAgent = user?.role === 'AGENT';
  const isB2BAgent = user?.role === 'B2B_AGENT_ADMIN' || user?.role === 'B2B_AGENT_USER';
  const isB2BAdmin = user?.role === 'B2B_AGENT_ADMIN';
  const isB2BUser = user?.role === 'B2B_AGENT_USER';
  const isViewer = user?.role === 'VIEWER';
  
  // Show cost breakdown (internal users see costs, B2B users don't)
  const showCostBreakdown = !isB2BAgent || user?.show_cost_breakdown;
  // Every quoting role (B2B agents/admins, agency Admin, Staff/Agent) sees only the
  // Uplift for vehicles — never Rate/Day, Days or Total — regardless of
  // show_cost_breakdown. Vehicle rates are managed in the masters, not on a quote.
  const hideVehicleCosts = isB2BAgent || isAdmin || isAgent;

  // Can manage users (admin or B2B admin for their team)
  const canManageUsers = isAdmin;
  const canManageSubUsers = isB2BAdmin;
  
  // Get dashboard URL based on role
  const getDashboardUrl = () => {
    return '/dashboard'; // All users use same dashboard now
  };

  // Memoize context value
  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAdmin,
    isAgent,
    isB2BAgent,
    isB2BAdmin,
    isB2BUser,
    isViewer,
    showCostBreakdown,
    hideVehicleCosts,
    canManageUsers,
    canManageSubUsers,
    getDashboardUrl,
    // Feature access
    features,
    subscriptionStatus,
    hasFeature,
    getFeatureLimit,
    refreshFeatures,
  }), [user, loading, isAdmin, isAgent, isB2BAgent, isB2BAdmin, isB2BUser, isViewer, showCostBreakdown, hideVehicleCosts, canManageUsers, canManageSubUsers, features, subscriptionStatus, hasFeature, getFeatureLimit, refreshFeatures]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
